import React from "react";
import { Link } from "react-router-dom";
import { CalendarClock, AlertTriangle, ShieldAlert, Bell, Clock, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { HelpBubble } from "../HelpBubble";

export function RenewalsWidget({ renewals }) {
  if (!renewals) return null;

  return (
    <Card data-testid="renewal-widget">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-amber-600" />
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
            Expiring Contracts
          </CardTitle>
          <HelpBubble tip="Contracts expiring within 90 days. Critical = 7 days or less, Warning = 30 days or less. Only visible to users with management access." />
        </div>
        <Link to="/contract-management">
          <Button variant="ghost" size="sm" className="gap-1 text-slate-600 hover:text-slate-900" data-testid="renewal-view-all-btn">
            View All <ArrowRight size={14} />
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {/* Summary badges */}
        <div className="flex items-center gap-3 mb-4" data-testid="renewal-summary">
          {renewals.summary?.critical_count > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800" data-testid="renewal-critical-badge">
              <ShieldAlert size={13} className="text-red-600" />
              <span className="text-xs font-semibold text-red-700 dark:text-red-400">{renewals.summary.critical_count} Critical</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" data-testid="renewal-expiring-badge">
            <AlertTriangle size={13} className="text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">{renewals.summary?.expiring_contracts || 0} Expiring</span>
          </div>
          {renewals.summary?.pending_obligations > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800" data-testid="renewal-obligations-badge">
              <Bell size={13} className="text-blue-600" />
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">{renewals.summary.pending_obligations} Obligation{renewals.summary.pending_obligations !== 1 ? "s" : ""} due</span>
            </div>
          )}
          {renewals.summary?.pending_milestones > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800" data-testid="renewal-milestones-badge">
              <Clock size={13} className="text-violet-600" />
              <span className="text-xs font-semibold text-violet-700 dark:text-violet-400">{renewals.summary.pending_milestones} Milestone{renewals.summary.pending_milestones !== 1 ? "s" : ""} due</span>
            </div>
          )}
        </div>

        {/* Contract expiry list — show top 6 most urgent */}
        {renewals.expirations?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="renewal-list">
            {renewals.expirations.slice(0, 6).map((item) => {
              const urgencyStyles = {
                critical: "border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-800",
                warning: "border-amber-200 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-800",
                info: "border-slate-200 dark:border-slate-700",
              };
              const daysBadgeStyles = {
                critical: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
                warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
                info: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
              };
              return (
                <Link
                  key={item.contract_id}
                  to={`/contracts/${item.contract_id}`}
                  className={`flex items-start gap-3 p-3 rounded-sm border transition-colors hover:shadow-sm ${urgencyStyles[item.urgency]}`}
                  data-testid={`renewal-item-${item.contract_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{item.title}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge className={`rounded-sm text-[10px] font-semibold ${daysBadgeStyles[item.urgency]}`} data-testid={`renewal-days-${item.contract_id}`}>
                        {item.days_remaining === 0 ? "Today" : item.days_remaining === 1 ? "1 day" : `${item.days_remaining} days`}
                      </Badge>
                      <span className="text-[10px] text-slate-400">
                        {new Date(item.expiration_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </div>
                    {item.contract_type && <p className="text-[10px] text-slate-400 mt-1">{item.contract_type}</p>}
                  </div>
                  {item.urgency === "critical" && <ShieldAlert size={16} className="text-red-500 shrink-0 mt-0.5" />}
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-3 py-3 px-4 rounded-sm bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800" data-testid="renewal-all-clear">
            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
              <CalendarClock size={14} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">All clear</p>
              <p className="text-xs text-emerald-600/70 dark:text-emerald-500/70">No contracts expiring within the next 90 days</p>
            </div>
          </div>
        )}

        {renewals.expirations?.length > 6 && (
          <p className="text-xs text-slate-400 text-center mt-3">
            +{renewals.expirations.length - 6} more expiring contract{renewals.expirations.length - 6 !== 1 ? "s" : ""}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
